import React, {
  FocusEventHandler,
  FocusEvent,
  ReactNode,
  useCallback,
  useState,
  ChangeEvent,
  useContext
} from 'react';
import { Tooltip } from 'antd';
import cn from 'clsx';

import { Hint } from 'components/Hint';
import { FieldMetaState, UseFieldConfig, useField } from 'react-final-form';
import { noop } from 'lodash-es';
import styles from './FormControl.module.scss';

import { useAttributeLabel } from '../useAttributeLabel';
import { FormContext } from '../FormContext';

export interface FormControlBaseProps {
  className?: string;
  label?: ReactNode;
  hint?: string;
  noHint?: boolean;
  name: string;
  required?: boolean;
  noFloat?: boolean;
  onFocus?: FocusEventHandler<HTMLElement>;
  onBlur?: FocusEventHandler<HTMLElement>;
  readOnly?: boolean;
}

type Props<T> = FormControlBaseProps &
  UseFieldConfig<T> & {
    children:
      | React.ReactElement
      | ((args: {
          value: T;
          readOnly?: boolean;
          onChange: (e: ChangeEvent<unknown> | T | undefined) => void;
          floating?: boolean;
          onFocus?: FocusEventHandler<HTMLElement>;
          onBlur?: FocusEventHandler<HTMLElement>;
          touch?: () => void;
        }) => React.ReactElement);
  };

export function FormControl<T>({
  children,
  className,
  label: labelProps,
  required,
  hint: hintProp,
  noFloat,
  name,
  noHint,
  readOnly: propReadOnly,
  ...props
}: Props<T>) {
  const [floating, setFloating] = useState(false);
  const { onFocus: nativeOnFocus, onBlur: nativeOnBlur } = props;
  const {
    input: { value, onBlur, onFocus, onChange },
    meta
  } = useField<T>(name, props);
  const { error, touched } = meta as Omit<FieldMetaState<T>, 'error'> & {
    error?: string;
  };
  const { readOnly: formReadOnly } = useContext(FormContext);

  const readOnly = propReadOnly || formReadOnly;

  const { label, hint } = useAttributeLabel(name, {
    label: labelProps,
    hint: hintProp
  });

  const handleFocus = useCallback(
    (e: FocusEvent<HTMLElement>) => {
      nativeOnFocus?.(e);
      if (noFloat) return;
      onFocus(e);
      setFloating(true);
    },
    [nativeOnFocus, noFloat, onFocus]
  );

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLElement>) => {
      nativeOnBlur?.(e);
      if (noFloat) return;
      onBlur(e);
      setFloating(false);
    },
    [nativeOnBlur, noFloat, onBlur]
  );

  return (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control
    <label
      className={cn(styles.formControl, className, {
        [styles.floating]:
          floating ||
          ((Array.isArray(value)
            ? value.length > 0
            : value || (!value && value === 0)) &&
            !noFloat),
        [styles.noFloat]: noFloat
      })}
    >
      <span className={styles.label}>
        {label}
        {!noHint && hint && <Hint hint={hint} className={styles.hint} />}
        {required && <sup>*</sup>}
      </span>
      {typeof children === 'function'
        ? children({
            readOnly,
            floating,
            onFocus: readOnly ? noop : handleFocus,
            onBlur: readOnly ? noop : handleBlur,
            onChange: readOnly ? noop : onChange,
            value,
            touch: () => {
              onFocus();
              onBlur();
            }
          })
        : React.cloneElement(React.Children.only(children), {
            onFocus: readOnly ? noop : handleFocus,
            onBlur: readOnly ? noop : handleBlur,
            readOnly
          })}
      {touched && !!error && (
        <Tooltip visible color="red" title={error} destroyTooltipOnHide>
          <span className={styles.tooltipAnchor} />
        </Tooltip>
      )}
    </label>
  );
}
